/**
 * 浏览器端将图片压到限定边长并导出为 JPEG，减小上传体积。
 * 失败或压后更大时回退为原文件。
 */
export type CompressImageFileOptions = {
    maxWidth?: number;
    maxHeight?: number;
    /** JPEG 质量 0～1 */
    quality?: number;
};

const loadImageFromFile = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('image load failed'));
        };
        img.src = url;
    });

export async function compressImageFile(
    file: File,
    options: CompressImageFileOptions = {}
): Promise<File> {
    if (!file.type.startsWith('image/')) {
        return file;
    }

    const maxWidth = options.maxWidth ?? 1600;
    const maxHeight = options.maxHeight ?? 1600;
    const quality = options.quality ?? 0.82;

    try {
        let width: number;
        let height: number;
        let drawScaled: (ctx: CanvasRenderingContext2D, cw: number, ch: number) => void;
        let release: (() => void) | undefined;

        if (typeof createImageBitmap === 'function') {
            const bitmap = await createImageBitmap(file);
            width = bitmap.width;
            height = bitmap.height;
            drawScaled = (ctx, cw, ch) => {
                ctx.drawImage(bitmap, 0, 0, cw, ch);
            };
            release = () => bitmap.close();
        } else {
            const img = await loadImageFromFile(file);
            width = img.naturalWidth || img.width;
            height = img.naturalHeight || img.height;
            drawScaled = (ctx, cw, ch) => {
                ctx.drawImage(img, 0, 0, cw, ch);
            };
        }

        if (width < 1 || height < 1) {
            release?.();
            return file;
        }

        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        const w = Math.max(1, Math.round(width * ratio));
        const h = Math.max(1, Math.round(height * ratio));

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            release?.();
            return file;
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        drawScaled(ctx, w, h);
        release?.();

        const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
        });
        if (!blob) {
            return file;
        }

        const base = file.name.replace(/\.[^.]+$/i, '') || 'image';
        const out = new File([blob], `${base}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now()
        });

        if (out.size >= file.size) {
            return file;
        }
        return out;
    } catch {
        return file;
    }
}
